import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Crown, Circle, Search, UserPlus, ShieldAlert, Trash2, Trophy, Clock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { RankBadge } from "@/components/alliance/RankManager";

const fmtLogin = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "never";

function InviteBox({ reload, full }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => api.get(`/alliance/search-users?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data.users)).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [q]);

  const invite = async (uid) => {
    try { await api.post("/alliance/invite", { user_id: uid }); toast.success("Invite sent"); setResults((rs) => rs.map((u) => u.user_id === uid ? { ...u, invited: true } : u)); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (full) return <p className="text-[11px] text-zinc-600 mt-4">Alliance is full — upgrade the size to invite more.</p>;

  return (
    <div className="mt-4 pt-4 border-t border-zinc-800">
      <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">Invite a lanista</label>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input data-testid="invite-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…"
          className="w-full bg-zinc-900 border border-zinc-800 pl-9 pr-3 py-2 text-zinc-100 text-sm focus:border-amber-600 outline-none" />
      </div>
      {results && (
        <div className="mt-2 space-y-1" data-testid="invite-results">
          {results.length === 0 ? <p className="text-[11px] text-zinc-600 px-1">No available players found.</p> : results.map((u) => (
            <div key={u.user_id} className="flex items-center gap-2 text-sm px-1 py-1">
              <span className="text-zinc-200">{u.name}</span>
              {u.invited
                ? <span className="text-[11px] text-amber-400/70 ml-auto">Invited</span>
                : <button data-testid={`invite-user-${u.user_id}`} onClick={() => invite(u.user_id)} className="ml-auto text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1"><UserPlus size={13} /> Invite</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Roster({ a, isLeader, canManage, onManageRank, onKick, reload }) {
  const navigate = useNavigate();
  return (
    <section className="border border-amber-900/30 bg-zinc-950 p-5">
      <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2"><Users size={16} /> Roster</h3>
      <div className="space-y-2" data-testid="alliance-members">
        {a.members.map((m) => (
          <div key={m.user_id} className="border-b border-zinc-900 pb-2">
            <div className="flex items-center gap-2 text-sm">
              {m.is_leader ? <Crown size={14} className="text-amber-500 shrink-0" /> : <Circle className="text-zinc-700 shrink-0" size={14} />}
              <button data-testid={`member-name-${m.user_id}`} onClick={() => navigate(`/profile/${m.user_id}`)}
                className="text-zinc-100 hover:text-amber-400 transition-colors">{m.name}</button>
              {m.vip && <Crown size={11} className="text-amber-400" />}
              <RankBadge rank={m.rank} perms={m.perms} />
              <span className="text-[11px] text-zinc-600 ml-auto">{m.active_gladiators} glads</span>
              {isLeader && !m.is_leader && (
                <button data-testid={`rank-${m.user_id}`} onClick={() => onManageRank(m)} className="text-zinc-600 hover:text-sky-400 ml-1" title="Manage rank"><ShieldAlert size={14} /></button>
              )}
              {isLeader && !m.is_leader && (
                <button data-testid={`kick-${m.user_id}`} onClick={() => onKick(m.user_id, m.name)} className="text-zinc-600 hover:text-red-400 ml-1"><Trash2 size={14} /></button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 pl-6 text-[11px] text-zinc-600">
              <span data-testid={`member-lastlogin-${m.user_id}`} className="flex items-center gap-1" title="Last login">
                <Clock size={11} className="text-zinc-700" /> Last login: <span className="text-zinc-500">{fmtLogin(m.last_login)}</span>
              </span>
              <span data-testid={`member-contribution-${m.user_id}`} className="flex items-center gap-1" title="This month’s ranking contribution from this lanista’s top five gladiators">
                <Trophy size={11} className="text-amber-600/70" /> <span className="text-amber-400/80 tabular-nums">{m.season_points ?? 0}</span> pts this month <span data-testid={`member-counted-gladiators-${m.user_id}`} className="text-zinc-700">({m.season_counted_gladiators ?? 0}/5)</span>
              </span>
            </div>
          </div>
        ))}
      </div>
      {canManage && <InviteBox reload={reload} full={a.is_full} />}
    </section>
  );
}
